import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {useKeyboard} from '../../../../hooks/keyboard';
import {AreaModel} from '../../../../store/control/state';
import {useSelector} from 'react-redux';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {SelectItem} from '../../../../components/Select';
import {AREA_TYPES, ICON_TYPES} from '../AreaListScreen';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';
import {SettingReadOnlyRowComponent} from '../../../../components/Settings/SettingReadOnlyRowComponent';
import {SettingsDeleteButton} from '../../../../components/Settings/SettingsDeleteButton';
import {useLocalization} from "../../../../hooks/localization";
import { Configs } from '../../../../configs';
import './index.scss';

const BACK_URL = '/settings/floors-and-rooms';

export const AreaEditScreen = memo(function AreaEditScreen() {
    const {t} = useLocalization();
    const params = useParams<{ id: string }>();
    const {success, failure} = useMessage();
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [rpc] = useRPC();
    const history = useHistory();

    const areaList = useSelector(state => state.control.areaList);

    const [selected, setSelected] = useState<AreaModel>(null);
    const [name, setName] = useState('');
    const [type, setType] = useState<SelectItem<string>>(null);
    const [icon, setIcon] = useState<SelectItem<string>>(null);

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const isFieldsValid = useCallback((): boolean => {
        if (type == null || type.value == null) {
            failure(t('pls_select_type'));

            return false;
        }

        if (icon == null || icon.value == null) {
            failure(t('pls_select_icon'));

            return false;
        }

        if (name == null || name.length === 0) {
            failure(t('pls_enter_a_valid_name'));

            return false;
        }

        return true;
    }, [t, failure, name, type, icon]);

    const onSavePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const areaModel: AreaModel = {
            id: selected != null ? selected.id : null,
            name,
            // @ts-ignore
            type: type.value,
            icon_type: icon.value,
            parent_area_id: null,
        };

        return rpc.updateArea(areaModel)
            .then(() => {
                success(t(areaModel.id == null ? 'area_successfully_created' : 'area_successfully_edited', {label: type.label}));
                onBackPressed();
            }).catch(failure);
    }, [
        t,
        isFieldsValid,
        failure,
        name,
        onBackPressed,
        rpc,
        selected,
        success,
        type,
        icon
    ]);

    const onDeletePressed = useCallback(() => {
        return rpc.deleteArea(selected.id)
            .then(() => {
                success(t('area_successfully_deleted', {name: selected.name}));
                onBackPressed();
            })
            .catch(failure);
    }, [t, failure, onBackPressed, rpc, selected, success]);

    const buttons = useMemo(() => [
        {
            component: selected == null ? null :
                <SettingsDeleteButton
                    id={selected?.id}
                    type='area'
                    questionText={t('are_you_sure_to_delete_the_area', {name: selected?.name})}
                    questionTextRelational={message => t('this_will_be_deleted_with_this_message__continue', {name:selected?.name, message})}
                    onPress={onDeletePressed}/>,
        },
        {
            text: t('save'),
            className: 'green',
            onClick: onSavePressed,
        },
        {
            text: t('cancel'),
            className: 'red',
            url: BACK_URL,
        }], [t, selected, onSavePressed, onDeletePressed],
    );

    const TYPE_OPTION_LIST = useMemo<SelectItem<string>[]>(() =>
            Object.entries(AREA_TYPES).map(([type, name]) => ({value: type, label: t(name)})),[t]);

    const ICON_OPTION_LIST = useMemo<SelectItem<string>[]>(() =>
            Object.entries(ICON_TYPES).map(([type, name]) => ({value: type, label: t(name)})),[t]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
    }, [setActiveInput]);

    useEffect(() => {
        if (params.id == null) {
            return;
        }

        const area = areaList.find(floor => floor.id === params['id']);
        if (area == null) {
            return;
        }

        setSelected(area);
        setName(area.name);
        setType(TYPE_OPTION_LIST.find(option => option.value === area.type));
        setIcon(ICON_OPTION_LIST.find(option => option.value === area.icon_type));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (params.id != null) {
            return;
        }

        setIcon(ICON_OPTION_LIST[0]);
        setType(TYPE_OPTION_LIST[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PasswordContainer requiredRole='service' isFullscreen={true}>
            <SettingsPageContainer
                className='service-edit'
                buttons={buttons}
                backUrl={BACK_URL}
                currentInput={currentInput}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        {
                            renderIf(selected)(() =>
                            <>
                            <SettingReadOnlyRowComponent
                                title={t('type')}
                                value={t(AREA_TYPES[selected.type])}/>
                            </>,
                                () =>
                            <>
                                <SettingSelectComponent
                                    label={t('type')}
                                    options={TYPE_OPTION_LIST}
                                    onChange={setType}
                                    value={type}/>
                            </>,
                            )
                        }
                        <SettingSelectComponent
                            label={t('icon')}
                            options={ICON_OPTION_LIST}
                            onChange={setIcon}
                            value={icon}/>
                        <div className="room-icon-container">
                            <img src={`${Configs.IMAGE_PREFIX}`+icon?.value} alt="icons" title="page" />
                        </div>
                    </div>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            placeholder={t('write_area_name',{label:type == null ? t('area') : type.label})}
                            label={t('area_name_2',{label:type == null ? t('area') : type.label})}
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
        </PasswordContainer>
    );
});
